/**
 * The Backup board's visual grammar, in one place so the tiles, the heat
 * strip, the badges and the charts can never disagree with each other.
 *
 * **The backend does the classifying, this module does the rendering.** Every
 * job, run and site-day arrives carrying a normalised `status`, so the browser
 * never re-derives "is this failed?" from raw Veeam vocabulary — one
 * classifier, one answer. Threshold and tone mapping is all that lives here.
 *
 * Two rules from `.claude/rules/dashboard-design.md` shape it:
 *
 * - **§3.2 status is reserved and never colour-alone.** A backup outcome is a
 *   status scale, not a categorical one, so every entry carries an icon and a
 *   label alongside its colour, exactly as `SEVERITY_STATUS` does for alerts.
 * - **§5 no data ≠ zero.** `never-run` is not a failure, `unknown` is not a
 *   success, and a missing byte count is not 0. Each gets its own state rather
 *   than collapsing into a healthier-looking one.
 *
 * `missed` earns the special treatment it gets: an enabled job with no session
 * in the window is the failure mode the manual spreadsheet structurally cannot
 * produce (concept §2.1), so it is its own state, its own tile and its own
 * colour — never folded into "failed".
 */

import {
  CalendarOff,
  CircleCheck,
  CircleDashed,
  CircleEllipsis,
  CircleQuestionMark,
  CircleSlash,
  CircleX,
  Cloud,
  type IconComponent,
  Layers,
  Server,
  ShieldCheck,
  ShieldOff,
  TriangleAlert,
} from "@norican/icons";
import { chartColors, sequentialScale } from "@norican/react";

import type { BackupCoverageRow, BackupJobRow, BackupSiteDay } from "@/lib/api";
import { formatRelativeTime, type StatusTone, type StripTone } from "@/lib/charts/config";

/* ------------------------------------------------------------------------ *
 * Job / run outcome
 * ------------------------------------------------------------------------ */

/** The backend's normalised status union — the only outcome vocabulary the UI uses. */
export type BackupStatus = BackupJobRow["status"];

/** Order used wherever states are listed or stacked, worst first. */
export const STATUS_ORDER: readonly BackupStatus[] = [
  "failed",
  "missed",
  "warning",
  "success",
  "running",
  "never-run",
  "unknown",
  "disabled",
];

export interface BackupStatusStyle {
  /** Full label, e.g. "Did not run". */
  label: string;
  /** Compact label for axes, legends and badges. */
  short: string;
  /** `Badge` / `Statistic` tone. */
  tone: StatusTone;
  /** `StatusStrip.Cell` / `StatisticBar` tone. */
  stripTone: StripTone;
  /** Chart fill — status tokens and the danger/neutral scales, never the categorical ramp. */
  color: string;
  /** Icon to pair with the colour — status is never colour-alone. */
  icon: IconComponent;
  /** One line explaining what the state actually means. */
  meaning: string;
}

const dangerScale = sequentialScale("danger");
const neutralScale = sequentialScale("neutral");

export const BACKUP_STATUS_STYLE: Record<BackupStatus, BackupStatusStyle> = {
  failed: {
    label: "Failed",
    short: "Failed",
    tone: "danger",
    stripTone: "negative",
    color: "var(--norican-danger)",
    icon: CircleX,
    meaning: "The job ran and did not complete.",
  },
  missed: {
    label: "Did not run",
    short: "Missed",
    tone: "danger",
    stripTone: "negative",
    // A step off the danger token, so a missed job is distinguishable from a
    // failed one on a chart while staying unmistakably in the danger family.
    color: dangerScale[2] ?? "var(--norican-danger)",
    icon: CalendarOff,
    meaning: "Enabled, but no session in the lookback window. Nothing errored, so nothing alerted.",
  },
  warning: {
    label: "Warning",
    short: "Warning",
    tone: "warning",
    stripTone: "caution",
    color: "var(--norican-warning)",
    icon: TriangleAlert,
    meaning: "The job completed with warnings.",
  },
  success: {
    label: "Success",
    short: "Success",
    tone: "success",
    stripTone: "positive",
    color: "var(--norican-success)",
    icon: CircleCheck,
    meaning: "The job completed.",
  },
  running: {
    label: "Running",
    short: "Running",
    tone: "neutral",
    stripTone: "neutral",
    color: neutralScale[3] ?? "var(--norican-bg-muted)",
    icon: CircleEllipsis,
    meaning: "A session is in progress; the outcome is not known yet.",
  },
  "never-run": {
    label: "Never run",
    short: "Never run",
    tone: "danger",
    stripTone: "negative",
    color: dangerScale[1] ?? "var(--norican-danger)",
    icon: CircleDashed,
    meaning:
      "Enabled, and no session has ever been recorded. Not a failure, and worse than an old restore point — there is nothing to restore from.",
  },
  unknown: {
    label: "Unknown",
    short: "Unknown",
    tone: "neutral",
    stripTone: "neutral",
    color: neutralScale[2] ?? "var(--norican-bg-muted)",
    icon: CircleQuestionMark,
    meaning: "NOVA cannot classify this state. Grey, never green.",
  },
  disabled: {
    label: "Disabled",
    short: "Disabled",
    tone: "neutral",
    stripTone: "neutral",
    color: neutralScale[1] ?? "var(--norican-bg-muted)",
    icon: CircleSlash,
    meaning: "The job is switched off, so it is out of scope for the counts above.",
  },
};

const FALLBACK_STATUS: BackupStatusStyle = BACKUP_STATUS_STYLE.unknown;

/** Style for a status string, safe for values a source invents later. */
export function statusStyle(status: string | null | undefined): BackupStatusStyle {
  return BACKUP_STATUS_STYLE[status as BackupStatus] ?? FALLBACK_STATUS;
}

/** True for the states the "Failures now" panel and the failure filter cover. */
export function needsAttention(status: string | null | undefined): boolean {
  return status === "failed" || status === "missed" || status === "never-run";
}

/* ------------------------------------------------------------------------ *
 * Schedule expectation
 * ------------------------------------------------------------------------ */

/**
 * Known `schedule_kind` words. Deliberately a lookup with a raw-string
 * fallback rather than a union (§8): the collector derives these from whatever
 * Veeam's schedule object holds, and a kind nobody has seen yet must render as
 * itself, never blow up and never disappear.
 */
const SCHEDULE_LABEL: Record<string, string> = {
  daily: "Daily",
  monthly: "Monthly",
  continuous: "Continuous",
  manual: "Run manually",
  unknown: "Schedule unknown",
};

/** Human label for one `schedule_kind`, including the parameterised forms. */
export function scheduleLabel(kind: string): string {
  const exact = SCHEDULE_LABEL[kind];
  if (exact) {
    return exact;
  }
  if (kind.startsWith("weekly:")) {
    return `Weekly (${kind.slice("weekly:".length).split(",").join(", ")})`;
  }
  if (kind.startsWith("periodic:")) {
    return "Periodic";
  }
  return kind;
}

/**
 * The schedule expectation as one line — "Daily · expected every 24 h · last
 * ran 26 hr ago".
 *
 * This is what a bare "missed" badge cannot say, and all three facts were
 * already being collected and stored: the caption exists because "did not run"
 * is only actionable next to how often the job was supposed to.
 */
export function scheduleCaption(job: BackupJobRow): string {
  if (job.schedule_kind == null && job.expected_every_h == null) {
    // Two different silences, and they send someone to different places.
    return job.config_known === false
      ? "Unknown — the source could not read this job's configuration"
      : "Not reported by this source";
  }
  const parts: string[] = [];
  if (job.schedule_kind) {
    parts.push(scheduleLabel(job.schedule_kind));
  }
  if (job.expected_every_h != null) {
    parts.push(`expected every ${job.expected_every_h} h`);
  }
  if (job.last_start) {
    parts.push(`last ran ${formatRelativeTime(job.last_start)}`);
  }
  return parts.join(" · ");
}

/* ------------------------------------------------------------------------ *
 * Sources
 * ------------------------------------------------------------------------ */

const SOURCE_LABEL: Record<string, string> = {
  veeam: "Veeam",
  "azure-backup": "Azure Backup",
  "veeam-m365": "Veeam for M365",
};

export function sourceLabel(source: string | null | undefined): string {
  if (!source) {
    return "Unknown source";
  }
  return SOURCE_LABEL[source] ?? source;
}

/* ------------------------------------------------------------------------ *
 * Day rollups (the site heat strip)
 * ------------------------------------------------------------------------ */

/** `StatusStrip.Cell` status for one site-day. */
export type CellStatus = StripTone;

/**
 * Tint for a site-day. The backend already says `unknown` when nothing ran, and
 * that must read as a visible grey bar — a site that quietly stopped reporting
 * has to be visible, never absent and never green (concept §2.3).
 */
export function dayStatus(day: BackupSiteDay): CellStatus {
  return statusStyle(day.status).stripTone;
}

/** Hover title for a site-day — says "nothing reported" rather than implying health. */
export function dayTitle(site: string, day: BackupSiteDay): string {
  const date = formatDay(day.day);
  if (day.status === "unknown" || day.total === 0) {
    return `${site} · ${date} — nothing reported`;
  }
  const parts: string[] = [];
  if (day.succeeded > 0) {
    parts.push(`${day.succeeded} succeeded`);
  }
  if (day.warning > 0) {
    parts.push(`${day.warning} with warnings`);
  }
  if (day.failed > 0) {
    parts.push(`${day.failed} failed`);
  }
  return `${site} · ${date} — ${parts.join(", ") || `${day.total} sessions`}`;
}

/** Format a rollup day (`YYYY-MM-DD`) as a short local date. */
export function formatDay(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/* ------------------------------------------------------------------------ *
 * Coverage buckets (concept §3b — the source-agnostic question)
 * ------------------------------------------------------------------------ */

/**
 * The backend's coverage states. Deliberately **source-agnostic at the top**:
 * "backed up by something" is the question that matters, and "backed up by
 * Veeam" is an implementation detail (concept §4).
 */
export type CoverageState = BackupCoverageRow["state"];

/** Buckets in reading order: the finding first. */
export const COVERAGE_ORDER: readonly CoverageState[] = [
  "none",
  "orphaned",
  "both",
  "veeam",
  "azure-backup",
];

export interface CoverageStyle {
  label: string;
  short: string;
  tone: StatusTone;
  color: string;
  icon: IconComponent;
  /** Why this bucket matters — used as the caption under the donut. */
  meaning: string;
}

/**
 * Colour rationale, because this one chart legitimately mixes the two families
 * the rules keep apart (§3.2 / §3.3):
 *
 * - `none` and `orphaned` are **statuses**: a server backed up by nothing is
 *   broken, and a protection item guarding a machine that no longer exists is
 *   false assurance with a bill attached. Both wear danger-family tokens.
 * - `veeam` / `azure-backup` / `both` are **identity**: which system protects a
 *   server is a categorical fact, not a grade, so they take fixed positions on
 *   the categorical ramp. A filter cannot repaint them, because the assignment
 *   is by bucket and never by rank.
 */
export const COVERAGE_STYLE: Record<CoverageState, CoverageStyle> = {
  none: {
    label: "Backed up by nothing",
    short: "Unprotected",
    tone: "danger",
    color: "var(--norican-danger)",
    icon: ShieldOff,
    meaning: "No protection source claims it. This is the finding to action.",
  },
  orphaned: {
    label: "Orphaned protection item",
    short: "Orphaned",
    tone: "warning",
    color: dangerScale[2] ?? "var(--norican-warning)",
    icon: CircleQuestionMark,
    meaning:
      "A protection item whose source resource no longer resolves — paid for, protecting nothing, and inflating the protected count.",
  },
  both: {
    label: "Protected twice",
    short: "Both",
    tone: "warning",
    color: chartColors[2] ?? "var(--norican-warning)",
    icon: Layers,
    meaning: "Covered by two sources — safe, but paid for twice.",
  },
  veeam: {
    label: "Veeam only",
    short: "Veeam",
    tone: "success",
    color: chartColors[0] ?? "var(--norican-success)",
    icon: Server,
    meaning: "Protected by the on-premises Veeam jobs.",
  },
  "azure-backup": {
    label: "Azure Backup only",
    short: "Azure",
    tone: "success",
    color: chartColors[1] ?? "var(--norican-success)",
    icon: Cloud,
    meaning: "Protected by an Azure Backup recovery point.",
  },
};

/**
 * What the coverage donut charts. Not the same set as `CoverageState`, because
 * one state has to split before it means anything: an unprotected server is
 * either a finding or a decision, and drawing both in one danger-red slice is
 * what made the panel read as an estate in crisis when 27 of the 59 slices were
 * the backup servers themselves.
 */
export type CoverageBucket = CoverageState | "excluded";

/** Buckets in reading order: the finding first, the decision beside it. */
export const COVERAGE_BUCKET_ORDER: readonly CoverageBucket[] = [
  "none",
  "excluded",
  "orphaned",
  "both",
  "veeam",
  "azure-backup",
];

export const COVERAGE_BUCKET_STYLE: Record<CoverageBucket, CoverageStyle> = {
  ...COVERAGE_STYLE,
  excluded: {
    label: "Unprotected on purpose",
    short: "By design",
    // Neutral, not success: nothing is protecting these, which is fine but is
    // not a good outcome to be tinted green.
    tone: "neutral",
    color: "var(--norican-fg-muted)",
    icon: ShieldCheck,
    meaning:
      "Backup infrastructure, network appliances, the desktop pool and lab machines. Unprotected by design, listed with the reason, never counted as a finding.",
  },
};

export function coverageStyle(state: string | null | undefined): CoverageStyle {
  return COVERAGE_BUCKET_STYLE[state as CoverageBucket] ?? COVERAGE_STYLE.none;
}

/* ------------------------------------------------------------------------ *
 * Thresholds
 * ------------------------------------------------------------------------ */

/** A restore point older than this is worth a warning tone. */
export const RESTORE_POINT_WARNING_HOURS = 48;
/** Older than this and the recovery risk is a danger-toned number. */
export const RESTORE_POINT_DANGER_HOURS = 24 * 7;

/** Repository free space below this fraction is danger-toned. */
export const REPO_DANGER_FREE_PCT = 10;
/** Below this it is caution-toned. */
export const REPO_WARNING_FREE_PCT = 20;

/** Tone for a restore-point age in seconds; unknown stays neutral. */
export function restorePointTone(ageSec: number | null | undefined): StatusTone {
  if (ageSec == null) {
    return "neutral";
  }
  const hours = ageSec / 3600;
  if (hours >= RESTORE_POINT_DANGER_HOURS) {
    return "danger";
  }
  return hours >= RESTORE_POINT_WARNING_HOURS ? "warning" : "success";
}

/** Tone for a repository's free-space percentage; unknown stays neutral. */
export function repositoryTone(freePercent: number | null): StatusTone {
  if (freePercent == null) {
    return "neutral";
  }
  if (freePercent < REPO_DANGER_FREE_PCT) {
    return "danger";
  }
  return freePercent < REPO_WARNING_FREE_PCT ? "warning" : "success";
}

/**
 * Free space as a percentage from the backend's `used_pct`, or null when the
 * capacity is unknown. Null must never become 0 — an unreadable repository and
 * a full one are different emergencies.
 */
export function freePercent(usedPct: number | null | undefined): number | null {
  return usedPct == null ? null : Math.max(0, Math.min(100, 100 - usedPct));
}

/* ------------------------------------------------------------------------ *
 * Formatters
 * ------------------------------------------------------------------------ */

const EM_DASH = "—";

/** Binary byte sizes. Null renders as an em dash, never as "0 B". */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null) {
    return EM_DASH;
  }
  const units = ["B", "KiB", "MiB", "GiB", "TiB", "PiB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value >= 100 || unit === 0 ? 0 : 1)} ${units[unit]}`;
}

/** A duration in seconds. Null (still running, or unusable times) is an em dash. */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null) {
    return EM_DASH;
  }
  const total = Math.max(0, Math.round(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (hours > 0) {
    return `${hours} h ${minutes} min`;
  }
  if (minutes > 0) {
    return `${minutes} min`;
  }
  return `${total} s`;
}

/** Absolute local timestamp for grids and description lists. */
export function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) {
    return EM_DASH;
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return EM_DASH;
  }
  return date.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}
